/**
 * Pure validation and body-building logic for the manual quote-decline dialog.
 * No React, no i18next runtime — Vitest-friendly. Reuses the shared decline-reason primitive
 * {@link isFreeTextMandatory} from the Phase 3.1 model (no duplication of the OTHER-requires-free-text
 * rule).
 */

import {DeclineReason, DismissQuoteDTO} from "@/api/data-contracts";
import {isFreeTextMandatory} from "@/common/request-cards/decline-reason-logic";

/**
 * In-progress state of the manual quote-decline dialog: the currently selected reason (undefined
 * until the user picks one) and the current free-text input.
 */
export interface DismissDialogState {
    reason: DeclineReason | undefined;
    freeText: string | null;
}

/**
 * Returns true when the dialog may be confirmed: a reason is selected AND (the reason does not
 * require free text OR a non-blank free text is provided). {@link DeclineReason.NO_REASON_GIVEN} is
 * a valid, directly confirmable selection needing no free text; only {@link DeclineReason.OTHER}
 * mandates a non-blank explanation.
 *
 * @param state - the dialog's in-progress selection state
 */
export function isDismissConfirmable(state: DismissDialogState): boolean {
    if (state.reason === undefined) {
        return false;
    }
    if (!isFreeTextMandatory(state.reason)) {
        return true;
    }
    return state.freeText !== null && state.freeText.trim().length > 0;
}

/**
 * Builds the {@link DismissQuoteDTO} request body from a confirmed selection. The free text is
 * trimmed and only included when non-blank — a blank or null explanation is omitted entirely so the
 * backend receives no empty string (avoids the {@code @Size(min = 1)} constraint violation).
 *
 * @param reason - the selected decline reason (guarded by {@link isDismissConfirmable})
 * @param freeText - the current free-text input, or null when untouched
 */
export function toDismissQuoteBody(reason: DeclineReason, freeText: string | null): DismissQuoteDTO {
    const trimmed = freeText?.trim() ?? "";
    return trimmed.length > 0 ? {declineReason: reason, freeText: trimmed} : {declineReason: reason};
}
